import { LangText } from '@/components'
import { TOKEN_KEY } from '@/constants'
import { useRootStore } from '@/contexts'
import { apiReceiveCoupon, type MiniCouponResponse } from '@/service/api/couponPage'
import { preFixImgUrl } from '@/utils'
import { Button, message } from 'antd'
import dayjs from 'dayjs'
import jsCookie from 'js-cookie'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import CouponVisualBlock from './CouponVisualBlock'

/** 到期展示：仅年月日 + 文案 */
const formatEndDateOnly = (endTime?: string) => {
  if (!endTime) return ''
  const d = dayjs(endTime)
  if (d.isValid()) return d.format('YYYY.MM.DD')
  const head = endTime.split(/[\sT]/)[0]
  return head || ''
}

interface CouponCardProps {
  coupon: MiniCouponResponse
  /** 领取成功后的回调（如刷新列表） */
  onReceived?: () => void
}

const CouponCard = ({ coupon, onReceived }: CouponCardProps) => {
  const { t } = useTranslation()
  const { userStore } = useRootStore()
  const location = useLocation()
  const navigate = useNavigate()
  const [claiming, setClaiming] = useState(false)
  const [receivedLocal, setReceivedLocal] = useState(false)

  const minConsumeAmount = Number(coupon.minConsume || 0)
  const minSpendLabel =
    minConsumeAmount > 0
      ? t('1b095084', { amount: minConsumeAmount.toLocaleString() })
      : t('04b8f9bf')
  const thresholdLabel =
    minConsumeAmount > 0
      ? t('5e95ddb2', { amount: minConsumeAmount.toLocaleString() })
      : t('04b8f9bf')
  const endDateStr = formatEndDateOnly(coupon.endTime)
  const shortExpiry = endDateStr ? `${endDateStr} ${t('80b1bb8d')}` : '-'
  const fullValidity =
    coupon.startTime && coupon.endTime
      ? `${coupon.startTime}-${coupon.endTime}`
      : '-'
  const applicableStores =
    coupon.scopeType === 1 ? t('317f12ee') : t('71df1f25')
  const description = coupon.description || '-'
  const isReceived = Boolean(coupon.received) || receivedLocal

  const handleReceive = () => {
    const token = jsCookie.get(TOKEN_KEY)
    const isAuthenticated = userStore.user !== null || !!token
    if (!isAuthenticated) {
      navigate('/signin', { state: { from: location } })
      return
    }
    if (!coupon.couponId || claiming || isReceived) return
    setClaiming(true)
    apiReceiveCoupon({ couponId: coupon.couponId })
      .then(res => {
        if (res.code === 0) {
          message.success(t('9631869e'))
          setReceivedLocal(true)
          onReceived?.()
        } else {
          message.error(res?.msg || t('c959fc6f'))
        }
      })
      .catch((err) => {
        message.error(err?.msg || t('c959fc6f'))
      })
      .finally(() => {
        setClaiming(false)
      })
  }

  return (
    <div className='rounded-[8px] overflow-hidden bg-white shadow-[0_2px_12px_0_rgba(30,34,46,0.06)] px-4'>

      {/* 商家名 */}
      <div className='pt-4 pb-2'>
        <div className='text-[14px] leading-snug text-[#666666]'>
          {coupon.merchantName || '-'}
        </div>
      </div>

      <CouponVisualBlock
        discountType={coupon.discountType}
        discountValue={coupon.discountValue}
        minSpendLabel={minSpendLabel}
        couponTitle={coupon.couponName || '-'}
        shortExpiry={shortExpiry}
        rightExtra={
          coupon?.receiveDetails ? (
            <img
              src={preFixImgUrl('8f28bbf2cccf7beb348263fb53ae384d.png')}
              alt={t('9e5ab080')}
              className='h-[60px] w-[60px] shrink-0 object-contain select-none'
              draggable={false}
            />
          ) : undefined
        }
      />

      <div className='flex flex-col space-y-4 py-4 text-[13px] text-[#666666]'>
        <div className='flex gap-2 justify-between'>
          <LangText lngKey='6a464900' className='shrink-0 text-[#666]' />
          <span className='text-right text-[#333333]'>{thresholdLabel}</span>
        </div>
        <div className='flex gap-2 justify-between'>
          <LangText lngKey='12535291' className='shrink-0 text-[#666]' />
          <span className='text-right text-[#333333]'>{fullValidity}</span>
        </div>
        <div className='flex gap-2 justify-between'>
          <LangText lngKey='4591f4ff' className='shrink-0 text-[#666]' />
          <span className='text-right text-[#333333]'>
            {(coupon.limitPerUser && Number(coupon.limitPerUser) > 0) ? (
              <>
                {coupon.limitPerUser}
                <LangText lngKey='92150017' className='ml-0.5' />
              </>
            ) : (
              <LangText lngKey='ae9216ec' />
            )}
          </span>
        </div>
        <div className='flex gap-2 justify-between'>
          <LangText lngKey='5a3fb5a0' className='shrink-0 text-[#666]' />
          <span className='text-right text-[#333333] line-clamp-2'>{applicableStores}</span>
        </div>
        <div className='flex gap-2 justify-between'>
          <LangText lngKey='634051e5' className='shrink-0 text-[#666]' />
          <span className='line-clamp-2 text-right text-[#333333]'>{description}</span>
        </div>
      </div>

      <div className='pb-4'>
        {isReceived ? (
          <Button
            block
            className='!h-[44px] !rounded-[22px] !border-[2px] !border-[#8883FF] !bg-white !text-[#5751FF] hover:!border-[#5B3EE6] hover:!text-[#5B3EE6]'
            onClick={() => navigate('/mine-coupon')}
          >
            <LangText className='text-[17px] font-bold' lngKey='9e5ab080' />
          </Button>
        ) : (
          <Button
            block
            type='primary'
            loading={claiming}
            disabled={claiming}
            className='!h-[44px] !rounded-[22px] !border-0 !bg-linear-to-r !from-[#9C59FF] !to-[#5751FF] hover:!opacity-95'
            onClick={handleReceive}
          >
            <LangText
              className='text-[17px] font-bold'
              lngKey={coupon?.receiveDetails ? 'b67bb069' : 'a8c3affc'}
            />
          </Button>
        )}
      </div>
    </div>
  )
}

export default CouponCard
